import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { documentsQueryKey } from "@/features/chat/documents/useDocuments";
import { threadDocumentsQueryKey } from "@/features/chat/documents/useThreadDocuments";
import { publishThreadDocumentsChanged } from "@/features/chat/documents/threadDocumentsEvents";
import { isDocumentsApiAvailable } from "@/services/documents/documentsApi";
import { uploadRejectionReason } from "@/services/documents/uploadPolicy";
import { isImageFile } from "./composerAccept";
import { createAttachmentId } from "./createAttachmentId";
import { runAttachmentUpload } from "./runAttachmentUpload";
import { startAttachmentUpload } from "./startAttachmentUpload";

function patchAttachment(setAttachments, id, patch) {
  setAttachments((prev) =>
    prev.map((item) => (item.id === id ? { ...item, ...patch } : item)),
  );
}

export function useComposerAttachmentUpload({ setAttachments, uploads, chat }) {
  const queryClient = useQueryClient();
  return useCallback(
    (attachment) => {
      // Images ride along as context in the prompt; they have no document
      // pipeline to enter, and posting them only ever produced a 415 and an
      // unexplained "Upload failed" chip.
      if (!isDocumentsApiAvailable() || isImageFile(attachment.file))
        return undefined;

      const onState = (patch) =>
        patchAttachment(setAttachments, attachment.id, patch);
      const reason = uploadRejectionReason(attachment.file);
      if (reason) {
        onState({ uploadState: "error", uploadError: reason });
        return undefined;
      }

      return startAttachmentUpload({
        attachment,
        chat,
        uploads,
        onState,
        run: runAttachmentUpload,
        idempotencyKey: createAttachmentId(),
        onUploaded: (chatId) => {
          queryClient.invalidateQueries({ queryKey: documentsQueryKey() });
          queryClient.invalidateQueries({
            queryKey: threadDocumentsQueryKey(chatId),
          });
          publishThreadDocumentsChanged(chatId);
        },
      });
    },
    [chat, queryClient, setAttachments, uploads],
  );
}
